import type {
  ComponentType,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  SVGProps,
} from 'react';
import classNames from 'classnames';
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline';
import './AlertCard.styles.scss';

export type tAlertCardVariant = 'error' | 'warning' | 'info' | 'success';

export type tAlertCardAction = {
  label: string;
  testId?: string;
  /** A button that runs `onClick`, or, with `href`, a link in a new tab. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  href?: string;
};

type tDataAttributes = Record<`data-${string}`, string | number | undefined>;

export type tAlertCardProps = {
  variant: tAlertCardVariant;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: ReactNode;
  /** One node per line; each line is its own `ls-alert-description`. */
  descriptions?: ReactNode[];
  /**
   * A short note under the title for a viewer who cannot act (INT-1653: the
   * seller). `locked` puts the padlock in front of it.
   */
  note?: { text: ReactNode; locked: boolean; testId?: string } | null;
  primaryAction?: tAlertCardAction | null;
  /**
   * AGI-885 (hallazgo 15) — one primary per screen. A card that yields the
   * primary to another surface keeps its action in the same slot, as a
   * tertiary button of the same height. Defaults to `'primary'`.
   */
  primaryEmphasis?: 'primary' | 'tertiary';
  secondaryAction?: tAlertCardAction | null;
  close?: {
    label: string;
    testId?: string;
    onClose: () => void;
  } | null;
  /**
   * Overrides the role the variant implies. The in-page surface keeps
   * `status` on every variant: none of its alerts is a system error.
   */
  role?: 'alert' | 'status';
  className?: string;
  /** `data-testid`, `data-alert-id`… on the `ls-alert` element itself. */
  dataAttributes?: tDataAttributes;
  titleTestId?: string;
  descriptionTestId?: string;
  /** Extra nodes inside the alert box (a live region, an open detail). */
  children?: ReactNode;
} & Pick<HTMLAttributes<HTMLDivElement>, 'id'>;

/**
 * INT-1653 — the one anatomy every Alert Center banner takes: the DS
 * `ls-alert` outline, a 44px icon tile, title and description, then the
 * buttons (secondary ghost first, primary last) and, when the alert can be put
 * down, the close control. Below `md` the buttons stack full width under the
 * text, primary first.
 *
 * The critical strip (`AlertBanner`) and the in-page surface (`AlertInPage`)
 * both render through here, so a P0 and a P1 never look like two component
 * families again (the left-stripe band they each drew is gone).
 */
export default function AlertCard(props: tAlertCardProps) {
  const {
    variant,
    icon: Icon,
    title,
    descriptions = [],
    note = null,
    primaryAction = null,
    primaryEmphasis = 'primary',
    secondaryAction = null,
    close = null,
    role,
    className,
    dataAttributes,
    titleTestId,
    descriptionTestId,
    children,
    id,
  } = props;

  const resolvedRole =
    role ?? (variant === 'error' || variant === 'warning' ? 'alert' : 'status');
  const hasActions = primaryAction !== null || secondaryAction !== null;

  return (
    <div className={classNames('AlertCard', className)}>
      <div
        id={id}
        className={`ls-alert ls-alert-${variant}`}
        role={resolvedRole}
        {...dataAttributes}
      >
        <span
          className={`AlertCard__Tile AlertCard__Tile--${variant}`}
          aria-hidden="true"
        >
          <Icon className="AlertCard__TileIcon" aria-hidden="true" />
        </span>

        <div className="ls-alert-content">
          <p className="ls-alert-title" data-testid={titleTestId}>
            {title}
          </p>
          {descriptions.map((line, index) => (
            <p
              key={index}
              className="ls-alert-description"
              data-testid={descriptionTestId}
            >
              {line}
            </p>
          ))}
          {note && (
            <p className="AlertCard__Note" data-testid={note.testId}>
              {note.locked && (
                <LockClosedIcon
                  className="AlertCard__NoteIcon"
                  aria-hidden="true"
                />
              )}
              <span>{note.text}</span>
            </p>
          )}
        </div>

        {hasActions && (
          <div className="AlertCard__Actions">
            {secondaryAction && (
              <AlertCardButton action={secondaryAction} kind="ghost" />
            )}
            {primaryAction && (
              <AlertCardButton action={primaryAction} kind={primaryEmphasis} />
            )}
          </div>
        )}

        {close && (
          <button
            type="button"
            className="ls-alert-close AlertCard__Close"
            aria-label={close.label}
            data-testid={close.testId}
            onClick={close.onClose}
          >
            <XMarkIcon aria-hidden="true" />
          </button>
        )}

        {children}
      </div>
    </div>
  );
}

type tAlertCardButtonProps = {
  action: tAlertCardAction;
  kind: 'primary' | 'tertiary' | 'ghost';
};

function AlertCardButton(props: tAlertCardButtonProps) {
  const { action, kind } = props;
  const className = `ls-btn ls-btn-${kind} AlertCard__Button`;

  if (action.href) {
    return (
      <a
        className={className}
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={action.testId}
        onClick={action.onClick}
      >
        {action.label}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      data-testid={action.testId}
      onClick={action.onClick}
    >
      {action.label}
    </button>
  );
}
